// module-access.guard.ts
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, map, of } from 'rxjs';
import { UserService } from '../../user/user.service';
import { PermissionService } from '../../services/permission.service';
import { ModuleKey, ModuleStatus } from '../../constants/module-status.enum';

@Injectable({
    providedIn: 'root'
})
export class ModuleAccessGuard implements CanActivate {
    constructor(
        private readonly _router: Router,
        private readonly _userService: UserService,
        private readonly _permissionService: PermissionService
    ) { }

    canActivate(_route: ActivatedRouteSnapshot): Observable<boolean> {
        // Get required module from route data
        const requiredModuleKey = _route.data['requiredModuleKey'] as ModuleKey;

        if (!requiredModuleKey) {
            console.error('ModuleAccessGuard: requiredModuleKey not found in route data', _route);
            this._router.navigate(['/extraspages/404']);
            return of(false);
        }

        // Check module access using PermissionService
        return this.checkModuleAccess(requiredModuleKey);
    }


    /**
     * Check module access using PermissionService
     * Always checks current permission status from PermissionService
     * @param moduleKey The module key to check
     * @returns Observable<boolean> indicating access permission
     */
    private checkModuleAccess(moduleKey: ModuleKey): Observable<boolean> {
        // Check if permissions are already loaded and active
        if (this._permissionService.hasModuleAccess(moduleKey)) {
            return of(true);
        }

        // Check if module status exists but is not ACTIVE
        const currentStatus = this._permissionService.getModuleStatus(moduleKey);
        if (currentStatus && currentStatus !== ModuleStatus.ACTIVE) {
            this._router.navigate(['/extraspages/404']);
            return of(false);
        }

        // If no permissions cached, fetch fresh data from backend
        return this._userService.get(true).pipe( // Pass true to skip business calls
            map(() => {
                // After fetching fresh data, check permission again
                if (this._permissionService.hasModuleAccess(moduleKey)) {
                    return true;
                }

                // Module not active or not found - redirect to 404
                this._router.navigate(['/extraspages/404']);
                return false;
            })
        );
    }
}